import {
  Directive,
  Input,
  ElementRef,
  HostListener,
  Renderer2,
  Inject,
  Injectable,
  RendererFactory2,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Styles } from './tooltip.styles';

//this code below is related to the tooltip css style added by injection
const STYLE = Styles;
@Injectable({
  providedIn: 'root',
})
class DirectiveStylesService {
  private readonly renderer: Renderer2;

  constructor(
    @Inject(DOCUMENT) private readonly documentRef: Document,
    @Inject(RendererFactory2) rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  addStyle(styles: string, attribute: string) {
    if (this.documentRef.head.querySelector(`style[${attribute}]`)) {
      return;
    }

    const style = this.renderer.createElement('style');

    this.renderer.setProperty(style, 'textContent', styles);
    this.renderer.setAttribute(style, attribute, '');

    this.documentRef.head.appendChild(style);
  }
}

@Directive({
  selector: '[psUiTooltip]',
})
export class TooltipDirective {
  @Input('psUiTooltip') title: string;
  @Input() placement: string;
  tooltip: any; // accepts the htmlElement and the null property at the same time, to recreate the element on hover
  offset = 10; // distance between host element and tooltip element

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Inject(DirectiveStylesService) directiveStyles: DirectiveStylesService
  ) {
    directiveStyles.addStyle(STYLE, 'psUiTooltip');
  }

  @HostListener('mouseenter') onMouseEnter() {
    if (!this.tooltip) {
      this.show();
    }
  }

  @HostListener('mouseleave') onMouseLeave() {
    if (this.tooltip) {
      this.hide();
    }
  }

  show() {
    this.create();
    this.setPosition();
    this.renderer.addClass(this.tooltip, 'ng-tooltip-show');
  }

  hide() {
    this.renderer.removeClass(this.tooltip, 'ng-tooltip-show');
    window.setTimeout(() => {
      if (this.tooltip) {
        this.renderer.removeChild(document.body, this.tooltip);
        this.tooltip = null;
      }
    }, 500);
  }

  create() {
    this.tooltip = this.renderer.createElement('span');
    this.renderer.appendChild(
      this.tooltip,
      this.renderer.createText(this.title)
    );

    this.renderer.appendChild(document.body, this.tooltip);
    this.renderer.addClass(this.tooltip, 'ng-tooltip');
    this.renderer.addClass(this.tooltip, `ng-tooltip-${this.placement}`);
  }

  setPosition() {
    // gets the host and tooltip size and location
    const hostPos = this.el.nativeElement.getBoundingClientRect();
    const tooltipPos = this.tooltip.getBoundingClientRect();

    // returns the relative position in the viewport.
    //When scrolling, the vertical scroll value should be reflected on the top of the tooltip element.
    const scrollPos =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    let top, left;

    if (this.placement === 'top') {
      top = hostPos.top - tooltipPos.height - this.offset;
      left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
    }

    if (this.placement === 'bottom') {
      top = hostPos.bottom + this.offset;
      left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
    }

    if (this.placement === 'left') {
      top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
      left = hostPos.left - tooltipPos.width - this.offset;
    }

    if (this.placement === 'right') {
      top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
      left = hostPos.right + this.offset;
    }
    this.renderer.setStyle(this.tooltip, 'top', `${top + scrollPos}px`);
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`);
  }
}
